import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { modelClass, cardListReferences, CardList } from '/imports/api/cardLists';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { Input } from '/imports/ui/Input';
import { JsonField, validateMongoQuery } from '/imports/ui/JsonField';
import { QueryPreview } from '/imports/ui/QueryPreview';

/**
 * Who uses this list. Editing a list changes every referencing fleet at once,
 * so the blast radius belongs on the page where the edit happens.
 */
const ReferencedBy = ({ listId }: { listId?: string }) => {
  const [refs, setRefs] = useState<{ fleets: { _id: string; name: string; fields: string[] }[]; settingsFields: string[] } | null>(null);

  useEffect(() => {
    if (!listId) { setRefs(null); return; }
    let cancelled = false;
    cardListReferences.call({ listId })
      .then((r) => { if (!cancelled) setRefs(r); })
      .catch(() => { if (!cancelled) setRefs(null); });
    return () => { cancelled = true; };
  }, [listId]);

  if (!listId) return null;
  const empty = refs && !refs.fleets.length && !refs.settingsFields.length;
  return (
    <div className="rounded-card border border-soft bg-panel-alt p-3">
      <div className="eyebrow text-fg-3">Referenced by</div>
      {!refs && <div className="mt-1 text-caption text-fg-3">Checking…</div>}
      {empty && <div className="mt-1 text-caption text-fg-3">Nothing yet. Point a fleet or Settings at this list to use it.</div>}
      {refs?.fleets.map((f) => (
        <div key={f._id} className="mt-1 text-ui">
          <Link className="text-primary hover:underline" to={`/fleets/${f._id}`}>{f.name}</Link>
          <span className="text-caption text-fg-3"> ({f.fields.join(', ')})</span>
        </div>
      ))}
      {refs && refs.settingsFields.length > 0 && (
        <div className="mt-1 text-ui">
          <Link className="text-primary hover:underline" to="/settings">Settings</Link>
          <span className="text-caption text-fg-3"> ({refs.settingsFields.join(', ')})</span>
        </div>
      )}
      {!empty && refs && (
        <div className="mt-2 text-caption text-warn">
          Saving changes here updates all of the above immediately.
        </div>
      )}
    </div>
  );
};

export const CardListPage = () => (
  <ModelFormPage
    modelClass={modelClass as any}
    permission={(user) => !!user?.isSuperUser}
    formSpec={() => [
      [{ field: 'name', label: 'Name', input: { type: Input }, tip: 'Unique strategy name, e.g. Pauper, Commander Staples.' }],
      [{ field: 'description', label: 'Description', input: { type: Input } }],
      [{
        field: 'query',
        input: (setUpdate, _update, document) => (
          <JsonField<CardList>
            field="query"
            label="Card Query"
            tip={'Mongo query against `cards`. Multiple selection expressions compose with $or, e.g. '
              + '{"$or":[{"setName":"HOB"},{"setName":"MSH","manBuyPrice":{"$gt":1}}]}'}
            initial={(document as any)?.query}
            validate={validateMongoQuery}
            emptyValue={null}
            setUpdate={setUpdate as any}
            className="h-32! font-mono text-caption"
          />
        ),
      }],
      [{
        field: 'query',
        input: (_setUpdate, update, document) => (
          <QueryPreview query={'query' in ((update ?? {}) as object)
            ? (update as any).query
            : (document as any)?.query} />
        ),
      }],
      [{
        field: 'query',
        input: (_setUpdate, _update, document) => (
          <ReferencedBy listId={(document as any)?._id} />
        ),
      }],
    ]}
  />
);
